import { Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { ChatRunProvider } from "../hooks/ChatRunProvider";
import { CanvasProvider, useCanvas } from "../hooks/useCanvas";
import { SourcesPanelProvider, useSourcesPanel } from "../hooks/useSourcesPanel";
import { CanvasPanel } from "./canvas/CanvasPanel";
import { SourcesPanel } from "./SourcesPanel";

// Rail widths must match each drawer's rendered width so the content reflows
// flush beside it (see AppShell's `--right-rail` padding). Canvas wins when both
// are somehow open; its width is user-resizable (see useCanvas).
const SOURCES_RAIL = "380px";

/**
 * Single owner of the shell's right rail (`--right-rail`): reserves room for
 * whichever drawer is open so the conversation reflows beside it instead of
 * being covered, and dismisses both drawers when you switch chats.
 */
function RightRailController() {
  const sources = useSourcesPanel();
  const canvas = useCanvas();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // A different chat shouldn't inherit the previous one's open Canvas / Sources.
  useEffect(() => {
    sources.close();
    canvas?.close();
    // Only react to route changes — close fns are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const rail = canvas?.isOpen ? `${canvas.width}px` : sources.isOpen ? SOURCES_RAIL : "0px";
  useEffect(() => {
    document.documentElement.style.setProperty("--right-rail", rail);
  }, [rail]);
  useEffect(
    () => () => {
      document.documentElement.style.removeProperty("--right-rail");
    },
    [],
  );

  return null;
}

/**
 * Layout for the chat routes: hosts the run controller so streaming state
 * survives the welcome (`/chat`) → thread (`/chat/$id`) navigation, plus the
 * shared Sources side panel and the Canvas artifact panel (opened from code
 * blocks), both scoped to the chat subtree and reflowed via the right rail.
 */
export function ChatLayout() {
  return (
    <ChatRunProvider>
      <CanvasProvider>
        <SourcesPanelProvider>
          <RightRailController />
          <Outlet />
          <SourcesPanel />
          <CanvasPanel />
        </SourcesPanelProvider>
      </CanvasProvider>
    </ChatRunProvider>
  );
}
